import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getEnv } from '../../lib/env';
import { getFeatures } from '../../lib/flotiq-client/external-services-requests';
import { checkResponseStatus } from '../../lib/flotiq-client/response-errors';
import { getFlotiqMainPageUrl } from '../../lib/helpers';

import { ArrowRightIcon } from '../../images/shapes';

import featuresPlaceholderData from './placeholder-data/features.json';

const QUERY_KEY = ['navigation_space', 'features'];
const PARAMS = {
  page: 1,
  limit: 1000,
  hydrate: 1,
  order_direction: 'asc',
  order_by: 'order',
};

export const useTopNav = () => {
  const {
    isLoading: requestLoading,
    data: featuresResponse,
    isPlaceholderData,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ signal }) => {
      const result = await getFeatures(undefined, undefined, PARAMS, {
        signal,
      });
      checkResponseStatus(result.body, result.status);
      return result;
    },
    enabled: true,
    staleTime: Infinity,
    gcTime: 0,
    retry: 0,
  });

  const isLoading = requestLoading || isPlaceholderData;

  const mainItems = useMemo(
    () => [
      {
        key: 'features',
        label: 'Features',
        items: [
          ...(featuresResponse?.body?.data || featuresPlaceholderData).map(
            ({ id, name, menu_icon }, index) => ({
              key: id,
              url: getFlotiqMainPageUrl(`/features/?feature=${index}`),
              label: name,
              iconUrl: menu_icon[0]
                ? `https://api.flotiq.com/image/60x0/${menu_icon[0].id}/${menu_icon[0].extension}`
                : '',
            }),
          ),
          {
            key: 'see-all-features',
            url: getFlotiqMainPageUrl(`/features/`),
            label: (
              <span className="lg:underline lg:text-blue lg:hover:text-blue-600 lg:text-lg">
                See all features{' '}
                <ArrowRightIcon className="inline w-4 m-2 text-blue" />
              </span>
            ),
          },
        ],
        isFeatures: true,
      },
      {
        key: 'solutions',
        label: 'Solutions',
        items: [
          {
            key: 'mobile',
            label: 'Mobile Apps',
            url: getFlotiqMainPageUrl('/solutions/mobile-apps/'),
          },
          {
            key: 'intranet',
            label: 'Intranet',
            url: getFlotiqMainPageUrl('/solutions/intranet/'),
          },
          {
            key: 'websites',
            label: 'Websites',
            url: getFlotiqMainPageUrl('/solutions/websites/'),
          },
        ],
      },
      {
        key: 'pricing',
        label: 'Pricing',
        url: getFlotiqMainPageUrl('/pricing/'),
      },
      {
        key: 'resources',
        label: 'Resources',
        items: [
          {
            key: 'blog',
            label: 'Blog',
            url: getFlotiqMainPageUrl('/blog/'),
          },
          {
            key: 'case-studies',
            label: 'Case studies',
            url: getFlotiqMainPageUrl('/case-studies/'),
          },
          {
            key: 'docs',
            label: 'Docs',
            url: getFlotiqMainPageUrl('/docs/'),
            newTab: true,
          },
          {
            key: 'faq',
            label: 'FAQ',
            url: getFlotiqMainPageUrl('/docs/faq/'),
            newTab: true,
          },
          {
            key: 'partners',
            label: 'Partners',
            url: getFlotiqMainPageUrl('/partners/'),
          },
          {
            key: 'starters',
            label: 'Starters',
            url: getFlotiqMainPageUrl('/starters/'),
          },
          {
            key: 'integrations',
            label: 'Integrations',
            url: getFlotiqMainPageUrl('/integrations/'),
          },
        ],
      },
    ],
    [featuresResponse?.body?.data],
  );

  const sideItems = useMemo(
    () => [
      {
        key: 'demo',
        label: 'Book a demo',
        url: getEnv('REACT_APP_FLOTIQ_CALCOM_URL'),
      },
      { key: 'login', label: 'Log in', url: '/login' },
    ],
    [],
  );

  return { isLoading, topNav: { mainItems, sideItems } };
};
